import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

function SheEarnsClaim() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [fromDate, setFromDate] = useState("2025-11-01");
  const [toDate, setToDate] = useState("2025-11-10");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/sheearns");
  };

  // Dummy aggregate earnings
  const aggregateEarnings = {
    period: "November 2025",
    totalEarned: 5000,
    claimable: 4500, // After deductions or holds
    status: "Ready to Claim" // or "Claimed" etc.
  };

  // Dummy claim history
  const dummyClaims = [
    { 
      sn: 1, 
      period: "October 2025", 
      amount: 3000, 
      status: "Transferred", 
      dateClaimed: "2025-10-15", 
      dateResolved: "2025-10-20",
      notes: "Successfully transferred to bank account ending ****1234"
    },
    { 
      sn: 2, 
      period: "September 2025", 
      amount: 2000, 
      status: "Rejected", 
      dateClaimed: "2025-09-25", 
      dateResolved: "2025-09-28",
      notes: "Insufficient documentation; please resubmit with bank proof"
    },
    { 
      sn: 3, 
      period: "August 2025", 
      amount: 2500, 
      status: "Accepted but Transfer in Process", 
      dateClaimed: "2025-08-10", 
      dateResolved: null,
      notes: "Bank verification in progress; expected completion by 2025-08-20"
    },
    { 
      sn: 4, 
      period: "July 2025", 
      amount: 1800, 
      status: "Under Process", 
      dateClaimed: "2025-07-05", 
      dateResolved: null,
      notes: "Reviewing earnings eligibility"
    },
    { 
      sn: 5, 
      period: "June 2025", 
      amount: 2200, 
      status: "I Claimed", 
      dateClaimed: "2025-06-18", 
      dateResolved: null,
      notes: "Claim submitted; awaiting initial review"
    },
    { 
      sn: 6, 
      period: "May 2025", 
      amount: 4000, 
      status: "Earned", 
      dateClaimed: null, 
      dateResolved: null,
      notes: "Pending claim initiation"
    }
  ];

  const filteredClaims = dummyClaims.filter((claim) => {
    const claimDate = new Date(claim.dateClaimed || claim.period.split(' ')[1] + '-01'); // Approximate filter
    const filterFrom = new Date(fromDate);
    const filterTo = new Date(toDate);
    return claimDate >= filterFrom && claimDate <= filterTo;
  });

  const totalClaimed = filteredClaims.reduce((sum, claim) => sum + claim.amount, 0);
  const recordCount = filteredClaims.length;

  const getStatusColor = (status) => {
    switch (status) {
      case "Earned": return "text-yellow-400";
      case "I Claimed": return "text-blue-400";
      case "Under Process": return "text-indigo-400";
      case "Accepted but Transfer in Process": return "text-green-400";
      case "Rejected": return "text-red-400";
      case "Transferred": return "text-emerald-400";
      default: return "text-gray-400";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Earned": return "⭐";
      case "I Claimed": return "📤";
      case "Under Process": return "⏳";
      case "Accepted but Transfer in Process": return "💳";
      case "Rejected": return "❌";
      case "Transferred": return "✅";
      default: return "ℹ️";
    }
  };

  if (!showHistory) {
    return (
      <div className="min-h-screen bg-black">
        <div className="min-h-screen flex flex-col bg-[#1a0b1e] text-pink-100 font-sans">
          {/* Header */}
          <header className="sticky top-0 z-50 bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 flex items-center justify-between shadow-lg">
            <div className="flex flex-col">
              <div className="text-xl lg:text-2xl font-bold text-pink-400">💖 SheEarns Claim</div>
              <div className="text-[10px] lg:text-lg text-yellow-200">
                My Earnings Claim
              </div>
            </div>

            {/* Desktop user controls */}
            <div className="hidden md:flex items-center gap-3">
              {isLoggedIn ? (
                <>
                  <div className="text-xs text-left text-pink-300">{userName}</div>
                  <button
                    onClick={handleLogout}
                    className="p-2 mt-2 rounded-md bg-rose-600 hover:bg-rose-700 transition"
                    title="Logout"
                  >
                    ⏻
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate("/login")}
                    className="px-3 py-1 rounded-full border border-pink-700 text-sm text-pink-200 hover:bg-pink-900"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate("/signup")}
                    className="px-3 py-1 rounded-full bg-pink-500 text-white text-sm hover:bg-pink-400"
                  >
                    Register
                  </button>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen((s) => !s)}
              className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-pink-300 bg-pink-500"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? "×" : "☰"}
            </button>
          </header>

          {/* Mobile menu content */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 animate-fadeIn">
              {isLoggedIn ? (
                <div className="flex flex-row items-center justify-center gap-3">
                  <span className="text-pink-300 font-medium whitespace-nowrap h-7">
                    Welcome, {userName}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="rounded-md bg-rose-600 hover:bg-rose-700 w-7 h-7 flex items-center justify-center text-white"
                    title="Logout"
                  >
                    ⏻
                  </button>
                </div>
              ) : (
                <div className="flex flex-row items-center justify-center gap-6">
                  <Link to="/login" className="text-pink-300 hover:text-yellow-300">
                    Login
                  </Link>
                  <span className="text-pink-500">|</span>
                  <Link to="/signup" className="text-pink-300 hover:text-yellow-300">
                    Register
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 py-8 px-4 text-center">
            <div className="max-w-md mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold text-pink-400 mb-2">
                Earnings Claim Dashboard
              </h1>
              <div className="bg-gray-800 rounded-lg p-6 mb-8">
                <h2 className="text-xl font-semibold text-white mb-2">Aggregate Earnings</h2>
                <p className="text-gray-300 mb-2">Period: {aggregateEarnings.period}</p>
                <p className="text-3xl font-bold text-pink-400 mb-2">₹{aggregateEarnings.totalEarned.toLocaleString()}</p>
                <p className="text-sm text-yellow-300 mb-4">Claimable: ₹{aggregateEarnings.claimable.toLocaleString()}</p>
                <p className="text-xs text-gray-400">Status: {aggregateEarnings.status}</p>
              </div>
              <button
                onClick={() => setShowHistory(true)}
                className="bg-gradient-to-r from-pink-500 to-pink-600 text-white font-semibold py-3 px-8 rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition w-full sm:w-auto"
              >
                Claim Now
              </button>
              <div className="mt-6 text-sm text-gray-300">
                <p>Workflow Guide:</p>
                <ul className="text-left mt-2 space-y-1 text-xs">
                  <li>• <span className="text-yellow-400">Earned</span>: Your earnings are calculated and ready.</li>
                  <li>• <span className="text-blue-400">I Claimed</span>: Submit your claim with bank details.</li>
                  <li>• <span className="text-indigo-400">Under Process</span>: Initial review (1-3 days).</li>
                  <li>• <span className="text-green-400">Accepted but Transfer in Process</span>: Verification passed; transfer initiating (3-7 days).</li>
                  <li>• <span className="text-red-400">Rejected</span>: Issue found; resubmit with corrections.</li>
                  <li>• <span className="text-emerald-400">Transferred</span>: Funds sent to your account.</li>
                </ul>
              </div>
            </div>
          </main>

          {/* Footer */}
          <footer className="bg-[#2a0f2f] border-t border-pink-800 text-pink-300 py-4 mt-auto">
            <div className="mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="text-xs lg:text-sm">
                © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
              </div>
              <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
                <Link to="/about-us" className="text-white hover:text-pink-400">About</Link>
                <Link to="/privacy-policy" className="text-white hover:text-pink-400">Privacy Policy</Link>
                <Link to="/terms-of-use" className="text-white hover:text-pink-400">Terms of Use</Link>
                <Link to="/contact-us" className="text-white hover:text-pink-400">Contact</Link>
              </div>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="min-h-screen flex flex-col bg-[#1a0b1e] text-pink-100 font-sans">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 flex items-center justify-between shadow-lg">
          <div className="flex flex-col">
            <div className="text-xl lg:text-2xl font-bold text-pink-400">💖 SheEarns Claim</div>
            <div className="text-[10px] lg:text-lg text-yellow-200">
              My Earnings Claim History
            </div>
          </div>

          {/* Desktop user controls */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="text-xs text-left text-pink-300">{userName}</div>
                <button
                  onClick={handleLogout}
                  className="p-2 mt-2 rounded-md bg-rose-600 hover:bg-rose-700 transition"
                  title="Logout"
                >
                  ⏻
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="px-3 py-1 rounded-full border border-pink-700 text-sm text-pink-200 hover:bg-pink-900"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="px-3 py-1 rounded-full bg-pink-500 text-white text-sm hover:bg-pink-400"
                >
                  Register
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-pink-300 bg-pink-500"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </header>

        {/* Mobile menu content */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 animate-fadeIn">
            {isLoggedIn ? (
              <div className="flex flex-row items-center justify-center gap-3">
                <span className="text-pink-300 font-medium whitespace-nowrap h-7">
                  Welcome, {userName}
                </span>
                <button
                  onClick={handleLogout}
                  className="rounded-md bg-rose-600 hover:bg-rose-700 w-7 h-7 flex items-center justify-center text-white"
                  title="Logout"
                >
                  ⏻
                </button>
              </div>
            ) : (
              <div className="flex flex-row items-center justify-center gap-6">
                <Link to="/login" className="text-pink-300 hover:text-yellow-300">
                  Login
                </Link>
                <span className="text-pink-500">|</span>
                <Link to="/signup" className="text-pink-300 hover:text-yellow-300">
                  Register
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 py-6 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-2">
              <h1 className="text-xl lg:text-2xl font-bold text-pink-400">Claim History</h1>
              <button
                onClick={() => setShowHistory(false)}
                className="bg-gradient-to-r from-pink-500 to-pink-600 text-white font-semibold py-2 px-6 w-40 rounded-xl shadow-md hover:shadow-lg transition"
              >
                New Claim
              </button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-2 gap-4 mb-2">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="bg-gray-800 text-pink-100 px-3 py-2 rounded-lg border border-pink-500 focus:outline-none focus:border-pink-400"
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="bg-gray-800 text-pink-100 px-3 py-2 rounded-lg border border-pink-500 focus:outline-none focus:border-pink-400"
              />
            </div>

            {/* Totals */}
            <div className="bg-gray-800 rounded-lg p-4 mb-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-pink-400 font-semibold">Total Claims</p>
                  <p className="text-white">{recordCount}</p>
                </div>
                <div>
                  <p className="text-pink-400 font-semibold">Total Claimed Amount</p>
                  <p className="text-white">₹{totalClaimed.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-pink-400 font-semibold">Date Range</p>
                  <p className="text-white">{fromDate} to {toDate}</p>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-pink-500 bg-gray-800 rounded-lg">
                <thead>
                  <tr className="bg-pink-900">
                    <th className="border border-pink-500 p-3 text-left text-pink-200">SN</th>
                    <th className="border border-pink-500 p-3 text-left text-pink-200">Period</th>
                    <th className="border border-pink-500 p-3 text-left text-pink-200">Amount (₹)</th>
                    <th className="border border-pink-500 p-3 text-left text-pink-200">Status</th>
                    <th className="border border-pink-500 p-3 text-left text-pink-200">Date Claimed</th>
                    <th className="border border-pink-500 p-3 text-left text-pink-200">Date Resolved</th>
                    <th className="border border-pink-500 p-3 text-left text-pink-200">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClaims.map((claim) => (
                    <tr key={claim.sn} className="hover:bg-gray-700 transition">
                      <td className="border border-pink-500 p-3">{claim.sn}</td>
                      <td className="border border-pink-500 p-3">{claim.period}</td>
                      <td className="border border-pink-500 p-3 font-semibold text-green-400">₹{claim.amount.toLocaleString()}</td>
                      <td className="border border-pink-500 p-3">
                        <span className={`inline-flex items-center gap-1 ${getStatusColor(claim.status)}`}>
                          {getStatusIcon(claim.status)} {claim.status}
                        </span>
                      </td>
                      <td className="border border-pink-500 p-3">{claim.dateClaimed || "N/A"}</td>
                      <td className="border border-pink-500 p-3">{claim.dateResolved || "Pending"}</td>
                      <td className="border border-pink-500 p-3 text-sm text-gray-300 max-w-xs">{claim.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Workflow Best Practices Section */}
            <div className="mt-8 bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-pink-400 mb-4">Claim Workflow Best Practices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h3 className="font-semibold text-white mb-2">Before Claiming</h3>
                  <ul className="space-y-1 text-gray-300">
                    <li>• Verify earnings in My Income dashboard</li>
                    <li>• Ensure bank details are updated in Profile</li>
                    <li>• Minimum claim: ₹500</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-2">After Claiming</h3>
                  <ul className="space-y-1 text-gray-300">
                    <li>• Track status here; updates via email/SMS</li>
                    <li>• For rejections, resubmit within 7 days</li>
                    <li>• Transfers via IMPS/NEFT (1-3 business days)</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-400">
                <p>Support: Contact us at support@sheearns.com for queries. Claims processed Mon-Fri, 9AM-6PM IST.</p>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-[#2a0f2f] border-t border-pink-800 text-pink-300 py-4 mt-auto">
          <div className="mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-xs lg:text-sm">
              © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
            </div>
            <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
              <Link to="/about-us" className="text-white hover:text-pink-400">About</Link>
              <Link to="/privacy-policy" className="text-white hover:text-pink-400">Privacy Policy</Link>
              <Link to="/terms-of-use" className="text-white hover:text-pink-400">Terms of Use</Link>
              <Link to="/contact-us" className="text-white hover:text-pink-400">Contact</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default SheEarnsClaim;